import * as d3 from 'd3';
import { __, sprintf } from '@wordpress/i18n';
import { themeColor } from '../utils/theme-color';

const TOOLTIP_RADIUS = 72;
const TOOLTIP_OFFSET_X = 92;
const TOOLTIP_OFFSET_Y = -92;
const POINT_RADIUS = 6;
const POINT_RADIUS_HOVER = 9;
const CLUSTER_RADIUS = 12;
const CLUSTER_RADIUS_HOVER = 15;
const CLUSTER_SPREAD_RADIUS = 28;
const CLUSTER_STROKE_WIDTH = 2;
const LINE_HEIGHT = 13;
const FIT_MARGIN = 16;
const CARD_GAP = 40;
// Matches Tailwind's `md` breakpoint (768px), the same one used in index.php,
// so the layout and the interaction model switch together instead of
// drifting apart.
const MOBILE_QUERY = '(max-width: 767px)';
const POINT_RADIUS_MOBILE = 10;
const POINT_RADIUS_HOVER_MOBILE = 13;
const CLUSTER_RADIUS_MOBILE = 16;
const CLUSTER_RADIUS_HOVER_MOBILE = 19;
// Minimum width the map renders at on mobile, in px — deliberately wider
// than a phone viewport. The SVG's own scrollable wrapper (see
// `svgWrapper` below) scrolls horizontally to reach the rest, trading
// "whole map visible at once" for dots/text that are actually legible and
// tappable, which a mobile viewport is too narrow to give at full-width
// scaling.
const MOBILE_MAP_WIDTH = 800;
// Extra breathing room, in screen pixels, between the "selected" and "not
// selected" markers `renderOffshoreStack()` renders for the same island's
// offshore anchor — added on top of both markers' own hover radii (which
// already differ by viewport and by point-vs-cluster, see
// `POINT_RADIUS_HOVER`/`CLUSTER_RADIUS_HOVER` and their `_MOBILE` variants)
// so the two can never touch even at their largest, hovered size.
const OFFSHORE_GROUP_GAP = 8;

/**
 * Group points that share the exact same location (e.g. several case
 * studies falling back to the same municipio) so they can be rendered as
 * one cluster instead of fully overlapping, indistinguishable circles.
 * @param {Array} points Array of { id, lat, lng } points.
 * @return {Array} Array of groups — each an array of one or more points.
 */
function groupByLocation( points ) {
	const groups = new Map();
	points.forEach( ( point ) => {
		const key = `${ point.lat.toFixed( 6 ) },${ point.lng.toFixed( 6 ) }`;
		if ( ! groups.has( key ) ) {
			groups.set( key, [] );
		}
		groups.get( key ).push( point );
	} );
	return Array.from( groups.values() );
}

/**
 * Stable identity for a group of points, used as the key for tracking which
 * clusters are expanded across re-renders.
 * @param {Array} group Array of points sharing one location.
 */
function clusterKey( group ) {
	return group
		.map( ( point ) => point.id )
		.sort()
		.join( '-' );
}

/**
 * Hover-tooltip label for a cluster, e.g. "3 case studies in Adeje".
 * Deliberately uses `clusterLocation` (city/municipio only) rather than
 * `location` (which also includes the island, and is used for the card's
 * Location field) — the island is not shown in the cluster tooltip. Falls
 * back to a location-less phrasing if none of the points in the group have
 * a resolved city name.
 * @param {Array} group Array of points sharing one location.
 */
function clusterLabel( group ) {
	const location = group[ 0 ].clusterLocation;

	return location
		? sprintf(
				/* translators: 1: number of case studies, 2: municipio or island name */
				__( '%1$d case studies in %2$s', 'ted' ),
				group.length,
				location
		  )
		: sprintf(
				/* translators: %d: number of case studies */
				__( '%d case studies', 'ted' ),
				group.length
		  );
}

/**
 * Combine the islands geometry with the case-study points into a single
 * GeoJSON object so the projection is fit to whichever is larger — some
 * fallback points are deliberately placed just offshore, past the islands'
 * own bounds, and would otherwise be clipped out of the viewport.
 * @param {Object} islands GeoJSON FeatureCollection of island polygons.
 * @param {Array}  points  Array of { lat, lng } points.
 */
function buildFitSource( islands, points ) {
	return {
		type: 'FeatureCollection',
		features: [
			...islands.features,
			{
				type: 'Feature',
				geometry: {
					type: 'MultiPoint',
					coordinates: points.map( ( p ) => [ p.lng, p.lat ] ),
				},
			},
		],
	};
}

/**
 * canarias.islas.geojson ships its rings wound the opposite way from what
 * d3-geo expects (a common GIS-export quirk): d3.geoArea reports each
 * feature as covering nearly the whole sphere instead of a small island,
 * which breaks bounds/fitSize. Reverse any ring whose area comes out that
 * way so the geometry projects correctly.
 * @param {Object} geojson A FeatureCollection of Polygon features.
 */
function rewindPolygons( geojson ) {
	geojson.features.forEach( ( feature ) => {
		if ( ! feature.geometry || feature.geometry.type !== 'Polygon' ) {
			return;
		}
		feature.geometry.coordinates.forEach( ( ring ) => {
			if (
				d3.geoArea( { type: 'Polygon', coordinates: [ ring ] } ) >
				2 * Math.PI
			) {
				ring.reverse();
			}
		} );
	} );
	return geojson;
}

/**
 * Split a label into lines that roughly fit within maxWidth, based on the
 * given <text> element's font so measurement matches what's actually drawn.
 * @param {Object} textSelection D3 selection of the <text> element to measure against.
 * @param {string} label         Text to wrap.
 * @param {number} maxWidth      Maximum line width in SVG user units.
 */
function wrapLines( textSelection, label, maxWidth ) {
	const words = label.split( /\s+/ ).filter( Boolean );
	const lines = [];
	let line = [];

	const measure = ( str ) => {
		textSelection.text( str );
		return textSelection.node().getComputedTextLength();
	};

	words.forEach( ( word ) => {
		const candidate = [ ...line, word ].join( ' ' );
		if ( line.length && measure( candidate ) > maxWidth ) {
			lines.push( line.join( ' ' ) );
			line = [ word ];
		} else {
			line.push( word );
		}
	} );

	if ( line.length ) {
		lines.push( line.join( ' ' ) );
	}

	textSelection.text( '' );
	return lines;
}

export function initCaseStudyMap( container, config ) {
	if ( ! container || ! config || ! config.islandsUrl ) {
		return;
	}

	const orange = themeColor( '--color-orange', '#e3572a' );
	const blue = themeColor( '--color-blue', '#40569d' );
	const green = themeColor( '--color-green', '#3f783f' );
	const cream = themeColor( '--color-cream', '#efe7d6' );
	const points = Array.isArray( config.points ) ? config.points : [];

	/**
	 * Color for a single point: blue for selected case studies, green for
	 * not-selected. Defaults to blue/selected when `selected` is missing.
	 * @param {Object} d Point data with an optional `selected` boolean.
	 */
	function pointColor( d ) {
		return false === d.selected ? green : blue;
	}

	/**
	 * Color for a cluster marker: the shared color if every point in the
	 * group has the same `selected` state, otherwise blue as a neutral
	 * fallback for a mixed-state cluster. Each point spread out of an
	 * expanded cluster still renders in its own correct color via
	 * `renderPoint()`.
	 * @param {Array} group Points sharing this location.
	 */
	function clusterColor( group ) {
		const allSelected = group.every( ( d ) => false !== d.selected );

		if ( allSelected ) {
			return blue;
		}

		const allNotSelected = group.every( ( d ) => false === d.selected );
		return allNotSelected ? green : blue;
	}

	/**
	 * Whether the viewport is currently narrower than the `md` breakpoint.
	 * Re-checked on every call rather than cached, since `render()` already
	 * rebuilds every point/cluster from scratch on each `ResizeObserver`
	 * firing (including a resize that crosses the breakpoint), so no
	 * separate `matchMedia` change-listener is needed.
	 */
	function isMobileViewport() {
		return window.matchMedia( MOBILE_QUERY ).matches;
	}

	d3.select( container ).classed( 'relative', true );

	// The `<svg>` lives in its own wrapper, not directly in `container`, so
	// `overflow-x-auto` (needed on mobile — see `MOBILE_MAP_WIDTH` — to
	// scroll to a map wider than the viewport) applies only to the map
	// graphic. Applying it to `container` itself was tried first and
	// reverted: per the CSS overflow spec, setting only one axis to
	// anything other than `visible` forces the *other* axis's used value to
	// `auto` too if it would otherwise be `visible` — there is no
	// CSS-only way to keep one axis scrollable and the other genuinely
	// `visible` on the same element. That silently made `container` a
	// vertical scroll/clip box as well, clipping/scrolling the desktop
	// info card (`openDesktopCard()`, `position: absolute`, appended to
	// `container` — not this wrapper — for exactly this reason) whenever
	// it was taller than the map.
	const svgWrapper = d3
		.select( container )
		.append( 'div' )
		.attr( 'class', 'w-full overflow-x-auto' );

	// This `aspect-*` class is the SVG's permanent CSS sizing mechanism, not
	// just a pre-paint placeholder: a non-`auto` `aspect-ratio` on a
	// replaced element with `height: auto` always wins for sizing, even
	// after `render()` sets a real `viewBox` below. The constant is chosen
	// to match the islands+points geometry's own computed ratio (see
	// `aspect` below), so the two never visibly disagree and the class
	// reads as if it's "only" a placeholder. On mobile, `render()`
	// additionally sets this element's width/height directly as inline
	// styles (overriding `w-full`/`h-auto`) to a fixed pixel size wider
	// than its scrollable wrapper — see `MOBILE_MAP_WIDTH` — so this
	// class only governs sizing before that first real render there; on
	// desktop it's the permanent mechanism. Without an aspect class at
	// all, this empty `<svg>` (no `viewBox` yet, since that's only set
	// once the async GeoJSON fetch resolves and `render()` runs) would
	// fall back to the UA default replaced-element height of 150px
	// regardless of width, then snap to its true height once `render()`
	// sets `viewBox` — shifting everything below it (`#caseStudyMosaic`
	// and its pinned ScrollTrigger) by that delta, which reads as the page
	// "jumping" if that shift happens to land while the user is scrolling
	// past this point. `preserveAspectRatio="xMidYMin meet"` anchors the
	// rendered content to the top of this box instead of centering it, so
	// any leftover space (e.g. a brief mismatch between this placeholder
	// ratio and the real one) collects at the bottom — where the open
	// card sits — rather than splitting above and below the map. If the
	// underlying case-study data's geographic spread changes enough to
	// visibly drift from this ratio, update the constant rather than
	// removing it.
	const svg = svgWrapper
		.append( 'svg' )
		.attr( 'class', 'block w-full h-auto aspect-[1360/542]' )
		.attr( 'preserveAspectRatio', 'xMidYMin meet' )
		.on( 'click', () => closeCard() );

	const islandsLayer = svg.append( 'g' ).attr( 'class', 'islands' );
	const pointsLayer = svg.append( 'g' ).attr( 'class', 'points' );
	const tooltipLayer = svg
		.append( 'g' )
		.attr( 'class', 'tooltip-layer' )
		.attr( 'pointer-events', 'none' );
	const cardLineLayer = svg
		.append( 'g' )
		.attr( 'class', 'card-line-layer' )
		.attr( 'pointer-events', 'none' );

	let geojson = null;
	let aspect = null;
	// Only one cluster can be expanded at a time — opening one collapses
	// whichever other cluster was already open (see the click handler in
	// `renderCluster()`), so this tracks a single key rather than a Set.
	let openClusterKey = null;
	let openCardId = null;
	let cardEl = null;

	document.addEventListener( 'click', ( event ) => {
		if ( null !== openCardId && ! container.contains( event.target ) ) {
			closeCard();
		}
	} );

	function render() {
		if ( ! geojson ) {
			return;
		}

		const containerWidth = container.clientWidth;

		if ( ! containerWidth ) {
			return;
		}

		const mobile = isMobileViewport();
		// On mobile, never render narrower than `MOBILE_MAP_WIDTH`, even
		// though `container` itself stays at the (narrower) viewport width
		// — `#caseStudyMap`'s `overflow-x-auto` lets the wider SVG spill
		// out and scroll instead of being squeezed down to fit.
		const width = mobile
			? Math.max( containerWidth, MOBILE_MAP_WIDTH )
			: containerWidth;

		const fitSource = buildFitSource( geojson, points );

		if ( null === aspect ) {
			const probe = d3.geoMercator().fitSize( [ 1000, 1000 ], fitSource );
			const bounds = d3.geoPath( probe ).bounds( fitSource );
			aspect =
				( bounds[ 1 ][ 1 ] - bounds[ 0 ][ 1 ] ) /
				( bounds[ 1 ][ 0 ] - bounds[ 0 ][ 0 ] );
		}

		const height = Math.round( width * aspect );

		svg.attr( 'viewBox', `0 0 ${ width } ${ height }` );
		// Overrides the `w-full`/`h-auto` Tailwind classes with an explicit
		// pixel box on mobile only; `null` on desktop removes any
		// previously-set inline style (e.g. after a resize crosses the `md`
		// breakpoint) so the responsive Tailwind classes take back over.
		svg.style( 'width', mobile ? `${ width }px` : null );
		svg.style( 'height', mobile ? `${ height }px` : null );

		const projection = d3.geoMercator().fitExtent(
			[
				[ FIT_MARGIN, FIT_MARGIN ],
				[ width - FIT_MARGIN, height - FIT_MARGIN ],
			],
			fitSource
		);
		const path = d3.geoPath( projection );

		islandsLayer
			.selectAll( 'path' )
			.data( geojson.features )
			.join( 'path' )
			.attr( 'd', path )
			.attr( 'fill', orange )
			.attr( 'stroke', 'none' );

		tooltipLayer.selectAll( '*' ).remove();
		pointsLayer.selectAll( '*' ).remove();
		closeCard();

		groupByLocation( points ).forEach( ( group ) => {
			const [ cx, cy ] = projection( [ group[ 0 ].lng, group[ 0 ].lat ] );

			if ( 1 === group.length ) {
				renderPoint( pointsLayer, group[ 0 ], cx, cy, width, height );
			} else if ( group.every( ( d ) => d.offshore ) ) {
				renderOffshoreStack(
					pointsLayer,
					group,
					cx,
					cy,
					width,
					height
				);
			} else {
				renderCluster( pointsLayer, group, cx, cy, width, height );
			}
		} );
	}

	/**
	 * Render a single case-study point (used both for standalone points and
	 * for the individual points spread out of an expanded cluster).
	 * @param {Object} layer  D3 selection to append the point into.
	 * @param {Object} d      The point's data — { id, title, lat, lng, selected }.
	 * @param {number} cx     Projected x position.
	 * @param {number} cy     Projected y position.
	 * @param {number} width  Current SVG width, for tooltip clamping.
	 * @param {number} height Current SVG height, for tooltip clamping.
	 */
	function renderPoint( layer, d, cx, cy, width, height ) {
		const activate = ( event ) => {
			event.stopPropagation();
			if ( openCardId === d.id ) {
				closeCard();
			} else {
				openCard( d, cx, cy, width, height );
			}
		};

		const mobile = isMobileViewport();
		const baseR = mobile ? POINT_RADIUS_MOBILE : POINT_RADIUS;
		const hoverR = mobile ? POINT_RADIUS_HOVER_MOBILE : POINT_RADIUS_HOVER;

		const circle = layer
			.append( 'circle' )
			.attr( 'cx', cx )
			.attr( 'cy', cy )
			.attr( 'r', baseR )
			.attr( 'fill', pointColor( d ) )
			.attr( 'tabindex', 0 )
			.attr( 'role', 'button' )
			.attr( 'aria-label', d.title )
			.attr( 'class', 'cursor-pointer transition-all duration-150' )
			.on( 'click', activate )
			.on( 'keydown', ( event ) => {
				if ( 'Enter' === event.key || ' ' === event.key ) {
					event.preventDefault();
					activate( event );
				}
			} );

		// `mouseenter` is deliberately not wired on mobile: some mobile
		// browsers require a first tap to satisfy a hover/mouseenter-bound
		// element before a second tap fires the real `click`, which is what
		// made the info card unreachable by touch. Keyboard/assistive-tech
		// `focus` still shows the tooltip on every viewport (e.g. an
		// external keyboard on a narrow screen).
		if ( mobile ) {
			circle
				.on( 'focus', function () {
					d3.select( this ).attr( 'r', hoverR );
					showTooltip( this, d, width, height, pointColor( d ) );
				} )
				.on( 'blur', function () {
					d3.select( this ).attr( 'r', baseR );
					tooltipLayer.selectAll( '*' ).remove();
				} );
		} else {
			circle
				.on( 'mouseenter focus', function () {
					d3.select( this ).attr( 'r', hoverR );
					showTooltip( this, d, width, height, pointColor( d ) );
				} )
				.on( 'mouseleave blur', function () {
					d3.select( this ).attr( 'r', baseR );
					tooltipLayer.selectAll( '*' ).remove();
				} );
		}

		circle.append( 'title' ).text( d.title );
	}

	/**
	 * Render case studies that all fell back to the same island's offshore
	 * anchor point (`offshore: true`, see `ted_case_study_map_resolve_points()`
	 * in `inc/ted-case-study-map.php`) as up to two markers around that
	 * anchor — one for "selected" case studies, one for "not selected" —
	 * rather than one marker per point. Each category with more than one
	 * point renders as a `renderCluster()` badge (click-to-expand, same as a
	 * shared-city cluster); a category with exactly one point renders as a
	 * plain point. This replaced an earlier version that spread every point
	 * individually: with enough case studies on one island (5+), the fixed
	 * per-point gap, clamped to the map's vertical bounds, ran out of room
	 * and points started overlapping. Capping it at two (cluster) markers
	 * per island removes that ceiling.
	 * @param {Object} layer  D3 selection to append points into.
	 * @param {Array}  group  Points sharing the same offshore anchor.
	 * @param {number} cx     Projected x position of the anchor.
	 * @param {number} cy     Projected y position of the anchor.
	 * @param {number} width  Current SVG width, for tooltip clamping.
	 * @param {number} height Current SVG height, for tooltip/spread clamping.
	 */
	function renderOffshoreStack( layer, group, cx, cy, width, height ) {
		const mobile = isMobileViewport();

		const categoryGroups = [
			group.filter( ( d ) => false !== d.selected ),
			group.filter( ( d ) => false === d.selected ),
		].filter( ( categoryGroup ) => categoryGroup.length > 0 );

		// The radius the marker for this category renders at while hovered —
		// a cluster badge when the category has more than one point, a plain
		// point otherwise — used to keep the two category markers from ever
		// touching, at their largest size, regardless of how many points
		// either of them holds.
		const markerHoverRadius = ( categoryGroup ) => {
			if ( categoryGroup.length > 1 ) {
				return mobile
					? CLUSTER_RADIUS_HOVER_MOBILE
					: CLUSTER_RADIUS_HOVER;
			}
			return mobile ? POINT_RADIUS_HOVER_MOBILE : POINT_RADIUS_HOVER;
		};

		const spacing =
			2 === categoryGroups.length
				? markerHoverRadius( categoryGroups[ 0 ] ) +
				  markerHoverRadius( categoryGroups[ 1 ] ) +
				  OFFSHORE_GROUP_GAP
				: 0;

		categoryGroups.forEach( ( categoryGroup, i ) => {
			const offset = ( i - ( categoryGroups.length - 1 ) / 2 ) * spacing;
			const py = Math.min(
				Math.max( cy + offset, FIT_MARGIN ),
				height - FIT_MARGIN
			);

			if ( 1 === categoryGroup.length ) {
				renderPoint( layer, categoryGroup[ 0 ], cx, py, width, height );
			} else {
				renderCluster( layer, categoryGroup, cx, py, width, height );
			}
		} );
	}

	/**
	 * Render several points that share one location as a single cluster: a
	 * bigger point labelled with the count, styled like the tooltip text.
	 * Clicking it toggles between the collapsed marker and the individual
	 * points spread around it, each connected back to the marker by a thin
	 * line.
	 * @param {Object} layer  D3 selection to append the cluster into.
	 * @param {Array}  group  Points sharing this location.
	 * @param {number} cx     Projected x position of the shared location.
	 * @param {number} cy     Projected y position of the shared location.
	 * @param {number} width  Current SVG width, for tooltip/spread clamping.
	 * @param {number} height Current SVG height, for tooltip/spread clamping.
	 */
	function renderCluster( layer, group, cx, cy, width, height ) {
		const key = clusterKey( group );
		const expanded = openClusterKey === key;
		const clusterGroup = layer.append( 'g' ).attr( 'class', 'cluster' );
		const mobile = isMobileViewport();
		const clusterR = mobile ? CLUSTER_RADIUS_MOBILE : CLUSTER_RADIUS;
		const clusterHoverR = mobile
			? CLUSTER_RADIUS_HOVER_MOBILE
			: CLUSTER_RADIUS_HOVER;

		if ( expanded ) {
			group.forEach( ( d, i ) => {
				const angle = ( 2 * Math.PI * i ) / group.length - Math.PI / 2;
				const px = Math.min(
					Math.max(
						cx + CLUSTER_SPREAD_RADIUS * Math.cos( angle ),
						FIT_MARGIN
					),
					width - FIT_MARGIN
				);
				const py = Math.min(
					Math.max(
						cy + CLUSTER_SPREAD_RADIUS * Math.sin( angle ),
						FIT_MARGIN
					),
					height - FIT_MARGIN
				);

				clusterGroup
					.append( 'line' )
					.attr( 'x1', cx )
					.attr( 'y1', cy )
					.attr( 'x2', px )
					.attr( 'y2', py )
					.attr( 'stroke', pointColor( d ) )
					.attr( 'stroke-width', 1 );

				renderPoint( clusterGroup, d, px, py, width, height );
			} );
		}

		const label = clusterLabel( group );
		const expandHint = expanded
			? /* translators: %s: cluster label, e.g. "3 case studies in Adeje" */
			  sprintf( __( '%s — click to collapse', 'ted' ), label )
			: /* translators: %s: cluster label, e.g. "3 case studies in Adeje" */
			  sprintf( __( '%s — click to expand', 'ted' ), label );

		const groupColor = clusterColor( group );

		// `drop-shadow-md` gives every cluster marker a visible edge against
		// whichever other marker or island fill it happens to sit on top of —
		// without it, two nearby clusters (or a cluster over the orange
		// islands layer) can visually merge into one shape. The cream stroke
		// ring + slight opacity dip only show up once `expanded`, so the
		// marker itself signals "open" independent of the spread points and
		// leader lines around it (which can be hard to notice at small map
		// sizes).
		const clusterCircle = clusterGroup
			.append( 'circle' )
			.attr( 'cx', cx )
			.attr( 'cy', cy )
			.attr( 'r', clusterR )
			.attr( 'fill', groupColor )
			.attr( 'stroke', cream )
			.attr( 'stroke-width', expanded ? CLUSTER_STROKE_WIDTH : 0 )
			.attr( 'tabindex', 0 )
			.attr( 'role', 'button' )
			.attr( 'aria-label', expandHint )
			.attr(
				'class',
				`cursor-pointer transition-all duration-150 drop-shadow-md${
					expanded ? ' opacity-90' : ''
				}`
			)
			.on( 'click', () => {
				openClusterKey = expanded ? null : key;
				render();
			} );

		// See the matching comment in `renderPoint()`: `mouseenter` is
		// skipped on mobile so a tap isn't absorbed by hover emulation.
		if ( mobile ) {
			clusterCircle
				.on( 'focus', function () {
					clusterGroup.raise();
					d3.select( this ).attr( 'r', clusterHoverR );
					showTooltip(
						this,
						{ title: label },
						width,
						height,
						groupColor
					);
				} )
				.on( 'blur', function () {
					d3.select( this ).attr( 'r', clusterR );
					tooltipLayer.selectAll( '*' ).remove();
				} );
		} else {
			clusterCircle
				.on( 'mouseenter focus', function () {
					// SVG has no z-index — paint order follows document
					// order, so a cluster sitting earlier in `pointsLayer`
					// than a neighboring, overlapping one is stuck
					// rendering underneath it. `raise()` moves this
					// cluster's whole <g> (circle, count label, and any
					// expanded spread points/lines) to the end of its
					// parent's children, making it paint last/on top for as
					// long as it's hovered or focused.
					clusterGroup.raise();
					d3.select( this ).attr( 'r', clusterHoverR );
					showTooltip(
						this,
						{ title: label },
						width,
						height,
						groupColor
					);
				} )
				.on( 'mouseleave blur', function () {
					d3.select( this ).attr( 'r', clusterR );
					tooltipLayer.selectAll( '*' ).remove();
				} );
		}

		clusterGroup
			.append( 'text' )
			.attr( 'x', cx )
			.attr( 'y', cy )
			.attr( 'fill', cream )
			.attr( 'text-anchor', 'middle' )
			.attr( 'dominant-baseline', 'central' )
			.attr(
				'class',
				'uppercase text-xs/6 font-bold pointer-events-none'
			)
			.text( group.length );
	}

	/**
	 * Close the currently open info card, if any, and its connecting line.
	 */
	function closeCard() {
		cardLineLayer.selectAll( '*' ).remove();
		if ( cardEl ) {
			cardEl.remove();
			cardEl = null;
		}
		openCardId = null;
	}

	/**
	 * Append one "label: value" row to the card, skipping it entirely when
	 * the value is empty so the card never shows a field with nothing in it.
	 * @param {Object} card  D3 selection of the card container.
	 * @param {string} label Field label.
	 * @param {string} value Field value.
	 */
	function appendCardField( card, label, value ) {
		if ( ! value ) {
			return;
		}
		const row = card
			.append( 'div' )
			.attr(
				'class',
				'flex items-start justify-between gap-2 py-4 border-t border-cream nth-2:border-0'
			);
		row.append( 'span' )
			.attr( 'class', 'font-heading font-semibold uppercase shrink-0' )
			.text( label );
		row.append( 'span' ).attr( 'class', 'text-right' ).text( value );
	}

	/**
	 * Build the shared content (number, keywords, name, location,
	 * alliances, short description) of the info card into an already
	 * appended, empty card container — shared between the desktop
	 * point-anchored card and the mobile bottom sheet, which differ only in
	 * outer positioning/chrome, not content.
	 * @param {Object} card D3 selection of the (empty) card container.
	 * @param {Object} d    The point's data, including its `card` fields.
	 */
	function buildCardContent( card, d ) {
		const info = d.card || {};
		const cardBgClass = false === d.selected ? 'bg-green' : 'bg-blue';
		const pillClass = false === d.selected ? 'text-green' : 'text-blue';

		card.classed( cardBgClass, true ).classed(
			'text-cream rounded-2xl p-4 text-sm leading-snug',
			true
		);

		// An in-flow row placed before the rest of the card's content, rather
		// than a `position: absolute` button layered on top of it — the
		// header row below already fills its own top-right corner with
		// keyword pills, so an absolutely-positioned close button in that
		// same corner would sit behind/under them instead of being reliably
		// tappable/clickable.
		card.append( 'div' )
			.attr( 'class', 'flex justify-end mb-2' )
			.append( 'button' )
			.attr( 'type', 'button' )
			.attr( 'aria-label', __( 'Close', 'ted' ) )
			.attr(
				'class',
				'text-3xl leading-4 cursor-pointer hover:text-white'
			)
			.text( '×' )
			.on( 'click', () => closeCard() );

		const header = card
			.append( 'div' )
			.attr(
				'class',
				'flex items-start justify-between gap-2 mb-4 md:mb-20'
			);
		header
			.append( 'span' )
			.attr( 'class', 'font-heading text-4xl leading-none' )
			.text( info.number || '' );

		const keywordsWrap = header
			.append( 'div' )
			.attr( 'class', 'flex flex-wrap gap-1 justify-end' );
		( info.keywords || [] ).forEach( ( keyword ) => {
			keywordsWrap
				.append( 'span' )
				.attr(
					'class',
					`bg-white ${ pillClass } rounded px-2 py-0.5 text-xs uppercase font-bold`
				)
				.text( keyword );
		} );

		appendCardField( card, __( 'Name', 'ted' ), info.nameLong || d.title );
		appendCardField( card, __( 'Location', 'ted' ), d.location );
		appendCardField(
			card,
			__( 'Alliances', 'ted' ),
			( info.orgs || [] ).join( ', ' )
		);

		if ( info.descriptionShort ) {
			card.append( 'div' )
				.attr( 'class', 'pt-2 border-t border-cream w-full' )
				.html( info.descriptionShort );
		}
	}

	/**
	 * Open the desktop info card for a case-study point: a small card
	 * connected to the point by a thin line, positioned so it stays inside
	 * the map. Relies on the SVG's `viewBox` units matching `container`'s
	 * CSS pixels 1:1, which only holds at the desktop aspect ratio.
	 * @param {Object} d      The point's data, including its `card` fields.
	 * @param {number} cx     Projected x position of the point.
	 * @param {number} cy     Projected y position of the point.
	 * @param {number} width  Current SVG width, for clamping.
	 * @param {number} height Current SVG height, for clamping.
	 */
	function openDesktopCard( d, cx, cy, width, height ) {
		// No `max-height`/`overflow-y` here: a card taller than the map is
		// deliberately left to overflow past `container`'s own box (it's
		// `position: absolute`, so it doesn't push any layout around) rather
		// than scroll internally — an internal scrollbar on top of the
		// already-scrollable page reads as a worse experience than a card
		// that simply extends past the map.
		const card = d3
			.select( container )
			.append( 'div' )
			.attr( 'class', 'absolute max-w-[300px] invisible' )
			.style( 'left', '0px' )
			.style( 'top', '0px' )
			.on( 'click', ( event ) => event.stopPropagation() );

		buildCardContent( card, d );

		cardEl = card.node();

		const cardW = cardEl.offsetWidth;
		const cardH = cardEl.offsetHeight;

		let left = cx + CARD_GAP;
		let top = cy - CARD_GAP - cardH;
		let flippedX = false;

		if ( left + cardW > width ) {
			left = cx - CARD_GAP - cardW;
			flippedX = true;
		}
		if ( top < 0 ) {
			top = cy + CARD_GAP;
		}

		left = Math.min( Math.max( left, 0 ), Math.max( width - cardW, 0 ) );
		top = Math.min( Math.max( top, 0 ), Math.max( height - cardH, 0 ) );

		card.style( 'left', `${ left }px` )
			.style( 'top', `${ top }px` )
			.classed( 'invisible', false );

		// Always land on the vertical middle of whichever side (left/right)
		// faces the point, regardless of how the card was flipped vertically.
		const anchorX = flippedX ? left + cardW : left;
		const anchorY = top + cardH / 2;

		cardLineLayer
			.append( 'line' )
			.attr( 'x1', cx )
			.attr( 'y1', cy )
			.attr( 'x2', anchorX )
			.attr( 'y2', anchorY )
			.attr( 'stroke', pointColor( d ) )
			.attr( 'stroke-width', 1 );
	}

	/**
	 * Open the info card as a full-width bottom sheet, used on mobile
	 * instead of the point-anchored desktop card: touch-scale positioning
	 * precision and the point-relative leader line don't hold up at small
	 * map sizes, so the card is presented independently of the tapped
	 * point's position. Appended to `document.body` (not `container`) so
	 * it can never affect `#caseStudyMap`'s own `ResizeObserver`-measured
	 * box size.
	 * @param {Object} d The point's data, including its `card` fields.
	 */
	function openMobileCard( d ) {
		const card = d3
			.select( document.body )
			.append( 'div' )
			.attr(
				'class',
				'fixed inset-x-0 bottom-0 z-50 rounded-t-2xl p-4 pb-6 max-h-[70vh] overflow-y-auto shadow-lg'
			)
			.on( 'click', ( event ) => event.stopPropagation() );

		buildCardContent( card, d );

		cardEl = card.node();
	}

	/**
	 * Open the info card for a case-study point, dispatching to the
	 * desktop point-anchored card or the mobile bottom sheet depending on
	 * viewport width.
	 * @param {Object} d      The point's data, including its `card` fields.
	 * @param {number} cx     Projected x position of the point.
	 * @param {number} cy     Projected y position of the point.
	 * @param {number} width  Current SVG width, for clamping.
	 * @param {number} height Current SVG height, for clamping.
	 */
	function openCard( d, cx, cy, width, height ) {
		closeCard();
		tooltipLayer.selectAll( '*' ).remove();
		openCardId = d.id;

		if ( isMobileViewport() ) {
			openMobileCard( d );
		} else {
			openDesktopCard( d, cx, cy, width, height );
		}
	}

	function showTooltip( pointNode, d, width, height, color ) {
		tooltipLayer.selectAll( '*' ).remove();

		const cx = Number( d3.select( pointNode ).attr( 'cx' ) );
		const cy = Number( d3.select( pointNode ).attr( 'cy' ) );

		let tx = cx + TOOLTIP_OFFSET_X;
		let ty = cy + TOOLTIP_OFFSET_Y;

		if ( tx + TOOLTIP_RADIUS > width ) {
			tx = cx - TOOLTIP_OFFSET_X;
		}
		if ( ty - TOOLTIP_RADIUS < 0 ) {
			ty = cy - TOOLTIP_OFFSET_Y;
		}
		tx = Math.min( Math.max( tx, TOOLTIP_RADIUS ), width - TOOLTIP_RADIUS );
		ty = Math.min(
			Math.max( ty, TOOLTIP_RADIUS ),
			height - TOOLTIP_RADIUS
		);

		tooltipLayer
			.append( 'line' )
			.attr( 'x1', cx )
			.attr( 'y1', cy )
			.attr( 'x2', tx )
			.attr( 'y2', ty )
			.attr( 'stroke', color )
			.attr( 'stroke-width', 1 );

		tooltipLayer
			.append( 'circle' )
			.attr( 'cx', tx )
			.attr( 'cy', ty )
			.attr( 'r', TOOLTIP_RADIUS )
			.attr( 'fill', color );

		const text = tooltipLayer
			.append( 'text' )
			.attr( 'x', tx )
			.attr( 'fill', cream )
			.attr( 'text-anchor', 'middle' )
			.attr( 'class', 'uppercase text-xs/6 font-bold' );

		const lines = wrapLines( text, d.title, TOOLTIP_RADIUS * 1.6 );
		const startY = ty - ( ( lines.length - 1 ) * LINE_HEIGHT ) / 2;

		lines.forEach( ( line, i ) => {
			text.append( 'tspan' )
				.attr( 'x', tx )
				.attr( 'y', startY + i * LINE_HEIGHT )
				.text( line );
		} );
	}

	d3.json( config.islandsUrl ).then( ( data ) => {
		geojson = rewindPolygons( data );
		render();
	} );

	new ResizeObserver( () => render() ).observe( container );
}
